import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import classnames from 'classnames';
import notifier from '@albert-io/notifier';
import {pushQueryParams} from 'client/history';
import {callAction, callTargetedAction} from 'client/framework';
import {SupplementSubjectDropdownTemplate} from '../SupplementSubjectDropdownTemplate.react';
import {
  addToast,
  addGenericErrorToast,
  Heading,
  Input,
  WithTooltip,
  Icon,
  Banner,
  Text,
  copyToClipboard,
  Button as AtomicButton
} from '@albert-io/atomic';
import Button from 'sg/Button/Button.react';
import SimpleDropdown from 'generic/SimpleDropdown/SimpleDropdown.react';
import simpleDropdownActions from 'generic/SimpleDropdown/SimpleDropdown.actions';
import SimpleDropdownStore from 'generic/SimpleDropdown/SimpleDropdown.store';
import WindowConfirmStore from 'generic/WindowConfirm/WindowConfirmStore';
import ImageEditor from 'client/SupplementTypes/Image/ImageEditor.react';
import PassageEditor from 'client/SupplementTypes/Passage/PassageEditor.react';
import AudioEditor from 'client/SupplementTypes/Audio/AudioEditor.react';
/**
 * @todo Adding `video` supplements has been disabled since March 2016.
 */
// import VideoEditor from 'client/SupplementTypes/VideoSupplement/VideoSupplementEditor.react';
import SortableTableEditor from 'client/SupplementTypes/SortableTableSupplement/SortableTableEditor.react';
import FreeFormEditor from 'client/SupplementTypes/FreeForm/FreeFormEditor.react';
import WindowConfirm from 'client/generic/WindowConfirm/WindowConfirm.react';
import windowConfirmActions from 'generic/WindowConfirm/WindowConfirmActions';
import supplementEditorStore from './SupplementEditor.store';
import supplementManagerStore from 'client/Supplements/SupplementManager/SupplementManager.store';
import supplementEditorActions from './SupplementEditor.actions';
import supplementManagerActions from 'client/Supplements/SupplementManager/SupplementManager.actions';
import sessionStore from 'client/Session/SessionStore';
import {AuthoringSupplementModelV1} from 'resources/augmented/AuthoringSupplement/AuthoringSupplementModel.v1';
import {AuthoringSubjectModelV1} from 'resources/augmented/AuthoringSubject/AuthoringSubjectModel.v1';
import errorCodes from 'client/errorCodes';
import supplementActions from 'client/Supplement/SupplementActions';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {invalidatePartialInterest} from 'resources/mandark.resource';
import {isCroppedOrScaled} from 'client/SupplementTypes/Image/utils';
import './supplement-editor.scss';

/* SupplementEditor

 * Like its cousin SupplementSearch, SupplementEditor looks to Mandark to populate our Subjects
 * dropdown before proceeding with rendering. Once we've received our domain and subjects, we
 * check them against the question we're currently editing, and if we have one we prepopulate
 * our domain and subject fields to match our active question.

 * We perform a set of checks on each UNSAFE_componentWillUpdate to confirm that our two dropdowns
 * don't fall out of sync with one another.  We also confirm that we are rendering the correct
 * editor for the selected supplement type.

 * Saving is straightforward, while inserting awaits the promise returned by the model's save()
 * method and forwards on our newly minted supplement to be serialized and pasted into the
 * question content.
 */

function getSupplementQuery(id) {
  return resource('authoring_supplement_v1')
    .mandarkEndpoint(['authoring_supplements_v1', id])
    .include('authoring_subject_v1');
}
/* eslint-disable camelcase */
function getSubjectsQuery(id) {
  return resource('authoring_subject_v1')
    .mandarkEndpoint(['authoring_subjects_v1'])
    .fields({
      authoring_subject_v1: 'id,domain,name'
    })
    .filter(
      {
        id
      },
      !sessionStore.isSuper() && id
    )
    .pageSize('infinite')
    .sort('domain,name');
}

const subjectDropdownStore = new SimpleDropdownStore('SupplementEditorSubjectDropdownStore');
const typeDropdownStore = new SimpleDropdownStore('SupplementEditorTypeDropdownStore');
const windowConfirmStore = new WindowConfirmStore('SupplementEditorWindowConfirmStore');

const SupplementCodeSection = ({supplement, type}) => {
  if (type === 'image') {
    return <ImageSupplementCodesSection supplement={supplement} />;
  }

  return (
    <>
      <Heading size='s'>Supplement Code</Heading>
      <div className='u-display_flex'>
        <Input
          className='supplement-editor__code-input'
          value={`[s:${supplement.getId()}:${type}]`}
        />
        <div className='supplement-editor__code-copy-btn'>
          <AtomicButton
            variant='text'
            onClick={() =>
              copyToClipboard(`[s:${supplement.getId()}:${type}]`, () => {
                addToast({
                  message: 'Supplement Code Copied!',
                  type: 'success'
                });
              })
            }
          >
            Copy supplement code
          </AtomicButton>
        </div>
      </div>
    </>
  );
};

SupplementCodeSection.propTypes = {
  supplement: PropTypes.instanceOf(AuthoringSubjectModelV1),
  type: PropTypes.string
};

const ImageSupplementCodesSection = ({supplement}) => {
  if (isCroppedOrScaled(supplement)) {
    return (
      <>
        <Banner className='u-mar-b_2'>
          <Text color={null}>
            <Heading size='xs'>
              Looking for &quot;Viewable&quot; or &quot;Downloadable&quot; Supplement Codes?
            </Heading>
            Since this image is cropped (or scaled) the &quot;Downloadable&quot; and
            &quot;Viewable&quot; functionality will not work reliably for the end-user. You&apos;ll
            still be able to crop and scale the image here, but if you want to take advantage of
            supplement codes, we recommend editing the image prior to uploading.
          </Text>
        </Banner>
      </>
    );
  }

  return (
    <>
      <Heading size='s'>Image Supplement Codes</Heading>
      <Heading size='xs'>
        Viewable
        <WithTooltip
          content='A "viewable" image supplement will render as button. When clicked, it will follow the link to the supplement source directly.'
          zLevel='toast'
        >
          <Icon className='u-mar-l_1' size='standalone' icon='info-circle' />
        </WithTooltip>
      </Heading>
      <div className='u-display_flex'>
        <Input
          value={`[s:${supplement.getId()}:viewable]`}
          className='supplement-editor__code-input'
        />
        <div className='supplement-editor__code-copy-btn'>
          <AtomicButton
            variant='text'
            onClick={() => {
              copyToClipboard(`[s:${supplement.getId()}:viewable]`, () => {
                addToast({
                  title: 'Supplement code copied!',
                  type: 'success'
                });
              });
            }}
          >
            Copy viewable code
          </AtomicButton>
        </div>
      </div>
      <Heading size='xs'>
        Downloadable
        <WithTooltip
          content='A "downloadable" image supplement will render as button. When clicked, it will attempt to initiate the download of the supplement source.'
          zLevel='toast'
        >
          <Icon className='u-mar-l_1' size='standalone' icon='info-circle' />
        </WithTooltip>
      </Heading>
      <div className='u-display_flex'>
        <Input
          value={`[s:${supplement.getId()}:downloadable]`}
          className='supplement-editor__code-input'
        />
        <div className='supplement-editor__code-copy-btn'>
          <AtomicButton
            color='primary'
            variant='text'
            onClick={() => {
              copyToClipboard(`[s:${supplement.getId()}:downloadable]`, () => {
                addToast({
                  title: 'Supplement code copied!',
                  type: 'success'
                });
              });
            }}
          >
            Copy downloadable code
          </AtomicButton>
        </div>
      </div>
    </>
  );
};

ImageSupplementCodesSection.propTypes = {
  supplement: PropTypes.instanceOf(AuthoringSupplementModelV1)
};

// eslint-disable-next-line import/prefer-default-export
export const SupplementEditor = awaitMandarkQueries(
  (props) => {
    const options = {
      queries: {
        subjects: getSubjectsQuery(props.activeSubjectId)
      }
    };
    if (props.activeSupplementId) {
      options.queries.supplement = getSupplementQuery(props.activeSupplementId);
    }
    return options;
  },
  class SupplementEditor extends React.Component {
    static propTypes = {
      activeSubjectId: PropTypes.string,
      activeSupplementId: PropTypes.string,
      showInsertButton: PropTypes.bool.isRequired,
      subjects: ImmutablePropTypes.listOf(PropTypes.instanceOf(AuthoringSubjectModelV1)),
      supplement: PropTypes.instanceOf(AuthoringSupplementModelV1),
      type: PropTypes.string,
      typeOptions: ImmutablePropTypes.list
    };

    /* eslint-disable react/default-props-match-prop-types */
    static defaultProps = {
      showInsertButton: false
    };

    constructor() {
      super();

      /**
       * @todo We might want this to use `SupplementTypes/SuppplementTypeDefinitions` eventually. However,
       * based on how this is currently implemented we actually benefit from supplement editors only being bundled
       * if the user has access to the `SupplementEditor` (Authoring)
       */
      this.editorsByType = {
        image: ImageEditor,
        passage: PassageEditor,
        audio: AudioEditor,
        // video: VideoEditor,
        'sortable-table': SortableTableEditor,
        'free-form': FreeFormEditor
      };
    }

    /* eslint camelcase: ["error", {allow: ["^UNSAFE_"]}] */
    UNSAFE_componentWillMount() {
      const activeSupplement = this.props.activeSupplementId
        ? this.props.supplement
        : AuthoringSupplementModelV1.getDefaultModel().setType('image');
      callAction(supplementEditorActions.SET_ACTIVE_SUPPLEMENT, activeSupplement);

      const activeSubject = this.props.activeSupplementId
        ? this.props.supplement.getAuthoringSubject()
        : this.props.subjects.find((subject) => subject.getId() === this.props.activeSubjectId);
      callAction(supplementEditorActions.SET_ACTIVE_SUBJECT, activeSubject);
    }

    componentWillUnmount() {
      callTargetedAction({
        name: simpleDropdownActions.RESET_STORE,
        targetStore: subjectDropdownStore.getName()
      });
      callAction(supplementEditorActions.RESET_STORE);
    }

    onChange = (e) => {
      const element = e.target;
      callAction(supplementEditorActions.MODIFY_ACTIVE_SUPPLEMENT, {
        setter: element.dataset.setterMethod,
        value: element.value
      });
      callAction(supplementEditorActions.VALIDATE_SUPPLEMENT, [element.name]);
    };

    onTypeChange = (item) => {
      const activeSupplement = AuthoringSupplementModelV1.getDefaultModel().setType(item.get('id'));
      callAction(supplementEditorActions.SET_ACTIVE_SUPPLEMENT, activeSupplement);
      callAction(supplementEditorActions.VALIDATE_SUPPLEMENT, ['type']);
    };

    onCheckChange = (e) => {
      const element = e.target;
      callAction(supplementEditorActions.MODIFY_ACTIVE_SUPPLEMENT, {
        setter: element.dataset.setterMethod,
        value: element.checked
      });
      /* Invoking validator without any args will safely validate all required fields by default */
      callAction(supplementEditorActions.VALIDATE_SUPPLEMENT);
    };

    handleCancelClick() {
      pushQueryParams({
        supplementMode: 'search',
        supplement: ''
      });
    }

    handleDeleteClick = () => {
      callTargetedAction({
        name: windowConfirmActions.SHOW_MODAL,
        targetStore: windowConfirmStore.getName()
      });
    };

    invalidateTableResults() {
      invalidatePartialInterest({
        resourcePath: ['authoring_supplements_v1'],
        filter: {
          authoring_subject_v1: {
            id: this.props.activeSubjectId
          }
        }
      });
    }

    async onDeleteConfirm() {
      try {
        await supplementEditorStore.getActiveSupplement().delete();
        pushQueryParams({
          supplementMode: 'search',
          supplement: ''
        });
        addToast({
          type: 'success',
          title: 'Success!',
          message: 'Your supplemented has been deleted.'
        });
        this.invalidateTableResults();
      } catch (error) {
        /* eslint-disable no-shadow */
        const supplementInUseError = error.response.body.errors.find((error) => {
          return error.code === errorCodes.BAD_REQUEST.VALIDATION_ERROR.SUPPLEMENT_USED_IN_QUESTION;
        });

        if (supplementInUseError) {
          addToast({
            type: 'danger',
            title: supplementInUseError.title,
            message: supplementInUseError.detail
          });
        } else {
          logger.error('Failed deleting supplement: %s', error.message || 'unknown error');
          addGenericErrorToast();
        }
      }
    }

    /* eslint-disable consistent-return */
    handleSaveClick = async () => {
      // (1) Validate that required info is provided, including a subject id
      // (1a) Bail if any required fields are missing
      // (2) Make the post to Mandark
      // (3) Display a toast message
      callAction(supplementEditorActions.VALIDATE_SUPPLEMENT);

      try {
        if (supplementEditorStore.hasErrors()) {
          /* eslint-disable no-throw-literal */
          throw 'Please complete all fields and try again.';
        }

        const supplement = supplementEditorStore.getActiveSupplement();

        const response = await supplement
          .addRelationship({
            type: 'owner_v2',
            relation: sessionStore.getUserId(),
            condition: !supplement.existsOnServer()
          })
          .addRelationship({
            type: 'authoring_subject_v1',
            relation: supplementEditorStore.getActiveSubject(),
            condition: !this.props.activeSupplementId
          })
          .save({
            customQuery: {
              include: 'owner_v2,authoring_subject_v1'
            }
          });

        callAction(supplementEditorActions.SET_ACTIVE_SUPPLEMENT, response);
        callAction(supplementActions.ADD_SUPPLEMENT, response);
        addToast({
          message: 'Your changes have been saved.',
          title: 'Success!',
          type: 'success'
        });
        this.invalidateTableResults();
        return response;
      } catch (error) {
        if (supplementEditorStore.hasErrors()) {
          /* eslint-disable no-shadow */
          const errors = supplementEditorStore
            .getErrors()
            .toList()
            .filter((error) => error !== null);

          addToast({
            message:
              errors.size > 1 ? (
                <ul>
                  {errors.map((_error, key) => (
                    <li key={key}>{_error}</li>
                  ))}
                </ul>
              ) : (
                <p>{errors.first()}</p>
              ),
            title: 'Something went wrong',
            type: 'danger'
          });
        } else {
          logger.error('Failed uploading supplement: %s', error.message || 'unknown error');
          notifier.notify(error, {
            component: 'SupplementEditor',
            fingerprint: 'SupplementSaveFailure'
          });
          addGenericErrorToast();
        }
      }
    };

    getInsertButton() {
      // This button only displays if the component renders inside the question editor.
      // It takes our `Save` action and chains it to an action for inserting the
      // supplement into the question text.  If that action
      // throws an error, we swallow it with a `catch`.
      return this.props.showInsertButton ? (
        <Button
          className='supplement-editor__btn supplement-editor__btn--insert'
          color='gray'
          disabled={supplementEditorStore.isUploadPending()}
          onClick={() => {
            this.handleSaveClick()
              .then((supplement) => {
                callAction(supplementManagerActions.INSERT_SUPPLEMENT_AT_CURSOR_POSITION, {
                  fieldPath: supplementManagerStore.getCurrentEditorField(),
                  supplement
                });
                callAction(supplementManagerActions.SHOW_MODAL, false);
              })
              .catch();
          }}
          text='Save &amp; Insert'
        />
      ) : null;
    }

    getDeleteButton() {
      return supplementEditorStore.getActiveSupplement().existsOnServer() ? (
        <Button
          className='supplement-editor__btn supplement-editor__btn--cancel'
          color='red'
          onClick={this.handleDeleteClick}
          text='Delete'
        />
      ) : null;
    }

    render() {
      const activeSupplement = supplementEditorStore.getActiveSupplement();
      const activeSubjectId = !activeSupplement.existsOnServer()
        ? this.props.activeSubjectId
        : activeSupplement.getAuthoringSubject().getId();
      const errors = supplementEditorStore.getErrors();
      const type = activeSupplement.getType();
      const subjectDropdownClassnames = classnames(
        'supplement-editor__input supplement-editor__input--subject',
        {
          'supplement-editor__input--error':
            !this.props.activeSupplementId && !supplementEditorStore.getActiveSubject()
        }
      );
      const Editor = this.editorsByType[type];
      return (
        <div className='supplement-editor__wrapper'>
          <div className='supplement-editor'>
            <SimpleDropdown
              activeItemId={activeSubjectId}
              className={subjectDropdownClassnames}
              disabled={this.props.activeSupplementId}
              fullWidth
              hasOptionTemplate
              label='Subject'
              onChange={(item) => callAction(supplementEditorActions.SET_ACTIVE_SUBJECT, item)}
              options={this.props.subjects}
              optionTemplate={SupplementSubjectDropdownTemplate}
              placeholder='Choose one'
              storeName={subjectDropdownStore.getName()}
            />
            <SimpleDropdown
              activeItemId={type}
              className='supplement-editor__input supplement-editor__input--type'
              destroyStoreOnUnmount
              disabled={Boolean(this.props.activeSupplementId)}
              fullWidth
              label='Type'
              options={this.props.typeOptions}
              onChange={this.onTypeChange}
              placeholder='Choose one'
              storeName={typeDropdownStore.getName()}
            />
            <Editor
              supplement={activeSupplement}
              onChange={this.onChange}
              onContentChange={this.onContentChange}
              onCheckChange={this.onCheckChange}
              errors={errors}
            />
            <SupplementCodeSection type={this.props.type} supplement={activeSupplement} />
            <div className='supplement-editor__btn-wrapper'>
              <Button
                className='supplement-editor__btn supplement-editor__btn--cancel'
                color='gray'
                onClick={this.handleCancelClick}
                text='Back'
              />
              {this.getDeleteButton()}
              {this.getInsertButton()}
              <Button
                className='supplement-editor__btn supplement-editor__btn--save'
                color='gray'
                disabled={supplementEditorStore.isUploadPending()}
                onClick={this.handleSaveClick}
                text='Save'
              />
            </div>
          </div>
          <WindowConfirm
            confirmButtonColor='red'
            confirmButtonText='Delete'
            message='This action cannot be undone.'
            onConfirm={this.onDeleteConfirm}
            storeName={windowConfirmStore.getName()}
            title='Are you sure you want to delete this supplement?'
          />
        </div>
      );
    }
  }
);
